import { FC, useMemo, useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce.ts';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Alert } from 'react-daisyui';
import Routes from '@/router/Routes.ts';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import SelectionChip from '@/components/functional/SelectionChip/SelectionChip.tsx';
import SelectionRadio from '@/components/functional/SelectionRadio/SelectionRadio.tsx';
import StepFormInput from '@/components/functional/StepFormInput/StepFormInput.tsx';
import StepFormTextarea from '@/components/functional/StepFormTextarea/StepFormTextarea.tsx';
import StepProgressBar from '@/components/ui/StepProgressBar/StepProgressBar.tsx';
import StepFormActions from '@/components/shared/StepFormActions/StepFormActions.tsx';
import StepFormHint from '@/components/ui/StepFormHint/StepFormHint.tsx';
import StepFormSubtitle from '@/components/ui/StepFormSubtitle/StepFormSubtitle.tsx';
import StepFormTitle from '@/components/ui/StepFormTitle/StepFormTitle.tsx';
import Stepper from '@/components/shared/Stepper/Stepper.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import CommandSignalChip from '@/components/ui/CommandSignalChip/CommandSignalChip.tsx';
import useFrameworkPreview from '@/api/useFrameworkPreview.ts';
import useIntentCapture, { InsufficientCreditsError } from '@/api/useIntentCapture.ts';
import useSummaryPreview from '@/api/useSummaryPreview.ts';
import useOrgCredits from '@/api/useOrgCredits.ts';
import useOrgFrameworkPreviewContext, { buildClarifyCaseText } from '@/api/useOrgFrameworkPreviewContext.ts';
import {
  clearIntakeDraft,
  hasIntakeDraftContent,
  loadIntakeDraft,
  saveIntakeDraft,
  type IntakeDraftStep,
} from '@/utils/intakeDraftStorage.ts';

const steps = ['define-issue', 'intake-protocol', 'clarify-case', 'context-impact', 'case-ready'] as const;

type IntakeStepId = IntakeDraftStep;

const COMMON_INTAKE_PROGRESS_STEPS = 3;

import { CLARIFY_CHIP_KEYS } from '@/constants/intakeClarifyChips.ts';

const VISIBILITY_KEYS = ['visCompanyWide', 'visSpecificTeam', 'visProductLine', 'visMarketRegion'] as const;

const IMPACT_KEYS = ['impactSlowingGrowth', 'impactMarginPressure', 'impactExecutionDelays', 'impactStrategicRisk'] as const;

const TIME_SENSITIVITY_KEYS = ['timeImmediate', 'timeShortTerm', 'timeMediumTerm'] as const;

type CommonIntakeData = {
  issue_title?: string;
  short_description?: string;
  core_constraints?: string[];
  core_constraint_detail?: string;
  decision_needed?: string;
  visibility?: string[];
  impact?: string[];
  time_sensitivity?: string;
  ai_routing_result?: {
    framework: string;
    confidence: number;
  };
};

const CommonIntake: FC = () => {
  const savedDraft = useMemo(() => loadIntakeDraft(), []);
  const resumeStep =
    savedDraft && isIntakeStepId(savedDraft.step) && hasIntakeDraftContent(savedDraft.data)
      ? savedDraft.step
      : 'define-issue';
  const resumeData = savedDraft && hasIntakeDraftContent(savedDraft.data) ? savedDraft.data : undefined;

  return (
  <div className="flex w-full justify-center pb-8">
    <Stepper.Wrapper steps={steps} initialStep={resumeStep} initialContextData={resumeData}>
      <Stepper.Step<typeof steps> step="define-issue">
        <StepDefineIssue />
      </Stepper.Step>
      <Stepper.Step<typeof steps> step="intake-protocol">
        <StepIntakeProtocol />
      </Stepper.Step>
    <Stepper.Step<typeof steps> step="clarify-case">
        <StepClarifyCase />
      </Stepper.Step>
        <Stepper.Step<typeof steps> step="context-impact">
        <StepContextImpact />
      </Stepper.Step>
      <Stepper.Step<typeof steps> step="case-ready">
        <StepCaseReady />
      </Stepper.Step>
    </Stepper.Wrapper>
  </div>
  );
};

export default CommonIntake;

function StepDefineIssue() {
  const { t } = useTranslation();
  const { nextStep } = useStepperContext<typeof steps, CommonIntakeData>();

  return (
    <BrandCardSideContent centerContent>
      <div className="flex flex-col gap-4 w-full">
        <div className="flex flex-col gap-2">
          <StepFormTitle>{t('commonIntakePage.defineIssue.title')}</StepFormTitle>
          <StepFormSubtitle>{t('commonIntakePage.defineIssue.subtitle')}</StepFormSubtitle>
        </div>
        <LoadingButton
          loading={false}
          color="primary"
          className="w-full"
          type="button"
          onClick={() => {
            clearIntakeDraft();
            nextStep();
          }}
        >
          {t('commonIntakePage.defineIssue.start')}
        </LoadingButton>
      </div>
    </BrandCardSideContent>
  );
}

type IntakeProtocolFields = {
  issue_title: string;
  short_description: string;
};

function StepIntakeProtocol() {
  const { t } = useTranslation();
  const { nextStep, previousStep, setContextData, contextData } = useStepperContext<typeof steps, CommonIntakeData>();
  const d = contextData as Partial<CommonIntakeData> | null;
  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<IntakeProtocolFields>({
    mode: 'onChange',
    defaultValues: {
      issue_title: d?.issue_title ?? '',
      short_description: d?.short_description ?? '',
    },
  });

  const onSubmit = (data: IntakeProtocolFields) => {
    const merged: Partial<CommonIntakeData> = { ...(d ?? {}), ...data };
    setContextData(merged);
    saveIntakeDraft('clarify-case', merged);
    nextStep();
  };

  return (
    <BrandCardSideContent>
      <form className="flex flex-col flex-1 gap-6 w-full" onSubmit={handleSubmit(onSubmit)}>
        <StepProgressBar totalSteps={COMMON_INTAKE_PROGRESS_STEPS} current={1} />
        <div className="flex flex-col gap-2">
          <StepFormTitle>{t('commonIntakePage.protocol.title')}</StepFormTitle>
          <StepFormSubtitle>{t('commonIntakePage.protocol.subtitle')}</StepFormSubtitle>
        </div>
        <div className="flex flex-col gap-4 w-full">
          <div className="flex flex-col gap-1.5">
            <StepFormInput placeholder={t('commonIntakePage.protocol.issueTitle')} {...register('issue_title', { required: true })} />
            <StepFormHint>{t('commonIntakePage.protocol.issueTitleHint')}</StepFormHint>
          </div>
          <div className="flex flex-col gap-1.5">
            <StepFormInput placeholder={t('commonIntakePage.protocol.shortDescription')} {...register('short_description', { required: true })} />
            <StepFormHint>{t('commonIntakePage.protocol.shortDescriptionHint')}</StepFormHint>
          </div>
        </div>
        <StepFormActions onBack={previousStep} continueDisabled={!isValid} />
      </form>
    </BrandCardSideContent>
  );
}

type ClarifyCaseFields = {
  core_constraints: string[];
  core_constraint_detail: string;
  decision_needed: string;
};

function StepClarifyCase() {
  const { t } = useTranslation();
  const { nextStep, previousStep, setContextData, contextData } = useStepperContext<typeof steps, CommonIntakeData>();
  const d = contextData as Partial<CommonIntakeData> | null;
  const { data: orgContext, isLoading: isOrgContextLoading } = useOrgFrameworkPreviewContext();

  const chipOptions = useMemo(
    () =>
      CLARIFY_CHIP_KEYS.map((key) => ({
        key,
        label: t(`commonIntakePage.clarify.${key}`),
      })),
    [t]
  );

  const { register, handleSubmit, watch, setValue } = useForm<ClarifyCaseFields>({
    defaultValues: {
      core_constraints: d?.core_constraints ?? [],
      core_constraint_detail: d?.core_constraint_detail ?? '',
      decision_needed: d?.decision_needed ?? '',
    },
  });

  const coreConstraints = watch('core_constraints');
  const coreConstraintDetail = watch('core_constraint_detail');
  const decisionNeeded = watch('decision_needed');

  const caseText = buildClarifyCaseText(
    { issue_title: d?.issue_title, short_description: d?.short_description },
    {
      core_constraints: coreConstraints,
      core_constraint_detail: coreConstraintDetail,
      decision_needed: decisionNeeded,
    },
  );
  const debouncedCaseText = useDebounce(caseText, 500);

  const { data: clarifyPreview, isFetching: isClarifyPreviewFetching } = useFrameworkPreview(
    orgContext?.industry ?? '',
    orgContext?.strategic_orientation ?? '',
    debouncedCaseText,
    !!(orgContext?.industry || orgContext?.strategic_orientation || debouncedCaseText),
  );

  const toggleConstraint = (key: string) => {
    const next = coreConstraints.includes(key)
      ? coreConstraints.filter((x) => x !== key)
      : [...coreConstraints, key];
    setValue('core_constraints', next, { shouldValidate: true });
  };

  const onSubmit = (data: ClarifyCaseFields) => {
    const merged: Partial<CommonIntakeData> = {
      ...(d ?? {}),
      core_constraints: data.core_constraints,
      core_constraint_detail: data.core_constraint_detail.trim() || undefined,
      decision_needed: data.decision_needed,
      ai_routing_result: clarifyPreview?.primary_framework
        ? { framework: clarifyPreview.primary_framework, confidence: clarifyPreview.routing_confidence }
        : d?.ai_routing_result,
    };
    setContextData(merged);
    saveIntakeDraft('context-impact', merged);
    nextStep();
  };

  const canContinue = coreConstraints.length > 0 && decisionNeeded.trim().length > 0;

  return (
    <BrandCardSideContent>
      <form className="flex flex-col flex-1 gap-6 w-full" onSubmit={handleSubmit(onSubmit)}>
        <StepProgressBar totalSteps={COMMON_INTAKE_PROGRESS_STEPS} current={2} />
        {(isOrgContextLoading || isClarifyPreviewFetching || clarifyPreview?.primary_framework) && (
          <CommandSignalChip
            frameworkId={clarifyPreview?.primary_framework}
            confidence={clarifyPreview?.routing_confidence}
            isLoading={isOrgContextLoading || isClarifyPreviewFetching}
          />
        )}
        <div className="flex flex-col gap-2">
          <StepFormTitle>{t('commonIntakePage.clarify.title')}</StepFormTitle>
          <StepFormSubtitle>{t('commonIntakePage.clarify.subtitle')}</StepFormSubtitle>
        </div>
        <div className="flex flex-col gap-2">
          <StepFormHint>{t('commonIntakePage.clarify.coreConstraint')}</StepFormHint>
          <div className="grid grid-cols-2 gap-2">
            {chipOptions.map(({ key, label }) => (
              <SelectionChip
                key={key}
                label={label}
                selected={coreConstraints.includes(key)}
                onClick={() => toggleConstraint(key)}
              />
            ))}
          </div>
          <StepFormInput placeholder={t('commonIntakePage.clarify.typeHere')} {...register('core_constraint_detail')} />
        </div>
        <div className="flex flex-col gap-2">
          <StepFormHint>{t('commonIntakePage.clarify.decisionNeeded')}</StepFormHint>
          <StepFormTextarea placeholder={t('commonIntakePage.clarify.typeHere')} {...register('decision_needed', { required: true })} />
          <StepFormHint>{t('commonIntakePage.clarify.decisionHint')}</StepFormHint>
        </div>
        <StepFormActions onBack={previousStep} continueDisabled={!canContinue} />
      </form>
    </BrandCardSideContent>
  );
}

type ContextImpactFields = {
  visibility: string[];
  impact: string[];
  time_sensitivity: string;
};

function StepContextImpact() {
  const { t } = useTranslation();
  const { nextStep, previousStep, setContextData, contextData } = useStepperContext<typeof steps, CommonIntakeData>();
  const d = contextData as Partial<CommonIntakeData> | null;
  const visOptions = useMemo(
    () => VISIBILITY_KEYS.map((key) => ({ key, label: t(`commonIntakePage.context.${key}`) })),
    [t]
  );
  const impactOptions = useMemo(
    () => IMPACT_KEYS.map((key) => ({ key, label: t(`commonIntakePage.context.${key}`) })),
    [t]
  );
  const timeOptions = useMemo(
    () => TIME_SENSITIVITY_KEYS.map((key) => ({ key, label: t(`commonIntakePage.context.${key}`) })),
    [t]
  );

  const { handleSubmit, watch, setValue } = useForm<ContextImpactFields>({
    defaultValues: {
      visibility: d?.visibility ?? [],
      impact: d?.impact ?? [],
      time_sensitivity: d?.time_sensitivity ?? '',
    },
  });

  const visibility = watch('visibility');
  const impact = watch('impact');
  const timeSensitivity = watch('time_sensitivity');

  const toggleList = (field: 'visibility' | 'impact', key: string) => {
    const list = field === 'visibility' ? visibility : impact;
    const next = list.includes(key) ? list.filter((x) => x !== key) : [...list, key];
    setValue(field, next, { shouldValidate: true });
  };

  const onSubmit = (data: ContextImpactFields) => {
    const merged: Partial<CommonIntakeData> = { ...(d ?? {}), ...data };
    setContextData(merged);
    saveIntakeDraft('case-ready', merged);
    nextStep();
  };

  const canContinue = visibility.length > 0 && impact.length > 0 && timeSensitivity.length > 0;

  return (
    <BrandCardSideContent>
      <form className="flex flex-col flex-1 gap-6 w-full" onSubmit={handleSubmit(onSubmit)}>
        <StepProgressBar totalSteps={COMMON_INTAKE_PROGRESS_STEPS} current={3} />
        <div className="flex flex-col gap-2">
          <StepFormTitle>{t('commonIntakePage.context.title')}</StepFormTitle>
          <StepFormSubtitle>{t('commonIntakePage.context.subtitle')}</StepFormSubtitle>
        </div>
        <div className="flex flex-col gap-2">
          <StepFormHint>{t('commonIntakePage.context.visibility')}</StepFormHint>
          <div className="grid grid-cols-2 gap-2">
            {visOptions.map(({ key, label }) => (
              <SelectionChip
                key={key}
                label={label}
                selected={visibility.includes(key)}
                onClick={() => toggleList('visibility', key)}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <StepFormHint>{t('commonIntakePage.context.impact')}</StepFormHint>
          <div className="grid grid-cols-2 gap-2">
            {impactOptions.map(({ key, label }) => (
              <SelectionChip
                key={key}
                label={label}
                selected={impact.includes(key)}
                onClick={() => toggleList('impact', key)}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <StepFormHint>{t('commonIntakePage.context.timeSensitivity')}</StepFormHint>
          <div className="flex flex-col gap-2">
            {timeOptions.map(({ key, label }) => (
              <SelectionRadio
                key={key}
                label={label}
                selected={timeSensitivity === key}
                onClick={() => setValue('time_sensitivity', key, { shouldValidate: true })}
              />
            ))}
          </div>
        </div>
        <StepFormActions
          onBack={previousStep}
          continueDisabled={!canContinue}
        />
      </form>
    </BrandCardSideContent>
  );
}

function StepCaseReady() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { goToStep, contextData } = useStepperContext<typeof steps, CommonIntakeData>();
  const d = contextData as Partial<CommonIntakeData> | null;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { hasCredits, isLoading: isCreditsLoading } = useOrgCredits();
  const { mutateAsync: captureIntent, isPending: isCapturing } = useIntentCapture();

  const {
    data: summaryData,
    isLoading: isSummaryLoading,
  } = useSummaryPreview(d ?? {}, { enabled: hasCredits });

  // Prefer AI routing result from summary-preview (more accurate than rule-based preview)
  const finalFramework = summaryData?.framework_routed ?? d?.ai_routing_result?.framework ?? null;
  const finalConfidence = summaryData?.confidence_score ?? d?.ai_routing_result?.confidence ?? null;

  const onContinue = () => {
    void (async () => {
      setSubmitError(null);
      try {
        const result = await captureIntent(d ?? {});
        clearIntakeDraft();
        navigate(Routes.CLARITY_DIAGNOSTIC_LAYER, { state: { decisionId: result.decision_id } });
      } catch (error) {
        console.error(error);
        if (error instanceof InsufficientCreditsError) {
          setSubmitError(t('commonIntakePage.errors.noCredits'));
        } else {
          setSubmitError(error instanceof Error ? error.message : t('commonIntakePage.errors.captureFailed'));
        }
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <div className="flex flex-col flex-1 gap-6 w-full">
        <div className="flex flex-col gap-2">
          <StepFormTitle>{t('commonIntakePage.ready.title')}</StepFormTitle>
          <StepFormSubtitle>{t('commonIntakePage.ready.subtitle')}</StepFormSubtitle>
        </div>
        {(submitError || (!isCreditsLoading && !hasCredits)) && (
          <Alert status="error">
            {submitError ?? t('commonIntakePage.errors.noCredits')}
          </Alert>
        )}
        {!isCreditsLoading && !hasCredits && (
          <button
            type="button"
            className="font-dm text-sm font-bold text-[#0F1B2A] underline underline-offset-2 text-left w-fit"
            onClick={() => navigate(Routes.CREDITS)}
          >
            {t('homePage.purchaseCredits')}
          </button>
        )}
        <div className="rounded-2xl bg-[#F4F9FF] p-5 flex flex-col gap-3 font-dm text-sm text-black/70 leading-relaxed min-h-[80px]">
          {isCreditsLoading ? (
            <div className="flex flex-col gap-2 animate-pulse">
              <div className="h-3 bg-black/10 rounded w-full" />
              <div className="h-3 bg-black/10 rounded w-5/6" />
            </div>
          ) : !hasCredits ? (
            <p>{t('commonIntakePage.ready.summaryNoCredits')}</p>
          ) : isSummaryLoading ? (
            <div className="flex flex-col gap-2 animate-pulse">
              <div className="h-3 bg-black/10 rounded w-full" />
              <div className="h-3 bg-black/10 rounded w-5/6" />
              <div className="h-3 bg-black/10 rounded w-4/6" />
            </div>
          ) : (
            <p>{summaryData?.structured_summary}</p>
          )}
        </div>
        <CommandSignalChip
          frameworkId={finalFramework}
          confidence={finalConfidence}
          isLoading={hasCredits && isSummaryLoading}
        />
        <StepFormActions
          onBack={() => {}}
          submitType="button"
          onContinue={onContinue}
          continueLabel={t('commonIntakePage.ready.continueDiagnostic')}
          continueDisabled={!hasCredits || isCreditsLoading || isSummaryLoading || isCapturing}
          continueLoading={isCapturing}
          secondaryAction={
            <button
              type="button"
              className="font-dm font-bold text-sm text-[#0F1B2A] text-center py-2"
              onClick={() => goToStep('intake-protocol')}
            >
              {t('commonIntakePage.ready.editCase')}
            </button>
          }
        />
      </div>
    </BrandCardSideContent>
  );
}

function isIntakeStepId(step: string): step is IntakeStepId {
  return (steps as readonly string[]).includes(step);
}
